import { useEffect, useState } from 'react'
import {SlidersHorizontal, Plus, Trash2, GripVertical, Save, CheckCircle2, Info, ToggleLeft, ToggleRight} from 'lucide-react'
import EmpresaLayout from '@/components/EmpresaLayout'
import { useAuth } from '@/hooks/useAuth'
import { lumi } from '@/lib/lumi'
import { useCompanyAspects, type CompanyAspect, type CompanyAspectsDoc } from '@/hooks/useCompanyAspects'

function slugifyKey(label: string): string {
  const base = label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return `custom_${base || 'aspecto'}_${Date.now().toString().slice(-4)}`
}

function EmpresaAspectos() {
  const { user } = useAuth()
  const { fetchConfig, saveConfig } = useCompanyAspects()

  const [doc, setDoc] = useState<CompanyAspectsDoc | null>(null)
  const [aspects, setAspects] = useState<CompanyAspect[]>([])
  const [companyName, setCompanyName] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // Formulario de nuevo aspecto personalizado
  const [newLabel, setNewLabel] = useState('')
  const [newDesc, setNewDesc] = useState('')

  useEffect(() => {
    if (!user) return
    const load = async () => {
      setLoading(true)
      const [{ doc, aspects }] = await Promise.all([fetchConfig(user.userId)])
      setDoc(doc)
      setAspects(aspects)
      try {
        const { list } = await lumi.entities.companies.list({ filter: { userId: user.userId }, limit: 1 })
        const c = list[0] as any
        setCompanyName(c?.tradeName ?? c?.commercialName ?? '')
      } catch {
        // ignore
      }
      setLoading(false)
    }
    load()
  }, [user, fetchConfig])

  const toggle = (key: string) => {
    setAspects((prev) => prev.map((a) => (a.key === key ? { ...a, enabled: !a.enabled } : a)))
    setSaved(false)
  }

  const removeCustom = (key: string) => {
    setAspects((prev) => prev.filter((a) => a.key !== key))
    setSaved(false)
  }

  const addCustom = () => {
    const label = newLabel.trim()
    if (!label) return
    const aspect: CompanyAspect = {
      key: slugifyKey(label),
      label,
      description: newDesc.trim(),
      enabled: true,
      custom: true,
    }
    setAspects((prev) => [...prev, aspect])
    setNewLabel('')
    setNewDesc('')
    setSaved(false)
  }

  const handleSave = async () => {
    if (!user) return
    setSaving(true)
    try {
      await saveConfig({
        docId: doc?._id,
        companyUserId: user.userId,
        companyName,
        aspects,
        creator: user.userId,
      })
      // Releer para sincronizar el _id si fue creación
      const { doc: refreshed } = await fetchConfig(user.userId)
      setDoc(refreshed)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (e) {
      console.error('Error al guardar aspectos:', e)
    } finally {
      setSaving(false)
    }
  }

  const enabledCount = aspects.filter((a) => a.enabled).length

  return (
    <EmpresaLayout
      title="Aspectos de evaluación"
      subtitle="Personaliza qué aspectos evalúas a tus colaboradores cada mes"
    >
      {loading ? (
        <div className="flex h-64 items-center justify-center text-gray-400">Cargando configuración...</div>
      ) : (
        <div className="max-w-3xl space-y-6">
          {/* Aviso informativo */}
          <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
            <Info size={18} className="mt-0.5 shrink-0 text-blue-600" />
            <p className="text-sm text-blue-800">
              Activa o desactiva los aspectos base de Beeznz Work y agrega los propios de tu empresa.
              Los aspectos <strong>habilitados</strong> serán los que aparezcan al evaluar a tus colaboradores.
              El administrador puede ver esta configuración.
            </p>
          </div>

          {/* Resumen */}
          <div className="flex items-center gap-3 rounded-xl bg-slate-900 px-5 py-4 text-white">
            <SlidersHorizontal size={20} className="text-amber-400" />
            <div>
              <p className="text-sm text-slate-300">Aspectos que evalúas actualmente</p>
              <p className="text-2xl font-bold text-amber-400">{enabledCount}</p>
            </div>
          </div>

          {/* Lista de aspectos */}
          <div className="rounded-2xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-5 py-3">
              <h3 className="font-semibold text-slate-900">Aspectos disponibles</h3>
            </div>
            <ul className="divide-y divide-slate-100">
              {aspects.map((a) => (
                <li key={a.key} className="flex items-center gap-3 px-5 py-3.5">
                  <GripVertical size={16} className="shrink-0 text-slate-300" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium text-slate-800">{a.label}</p>
                      {a.custom ? (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                          Propio
                        </span>
                      ) : (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          Base
                        </span>
                      )}
                    </div>
                    {a.description ? <p className="truncate text-xs text-slate-400">{a.description}</p> : null}
                  </div>
                  <button
                    onClick={() => toggle(a.key)}
                    className="shrink-0"
                    title={a.enabled ? 'Desactivar' : 'Activar'}
                  >
                    {a.enabled ? (
                      <ToggleRight size={32} className="text-emerald-500" />
                    ) : (
                      <ToggleLeft size={32} className="text-slate-300" />
                    )}
                  </button>
                  {a.custom && (
                    <button
                      onClick={() => removeCustom(a.key)}
                      className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
                      title="Eliminar aspecto propio"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Agregar aspecto personalizado */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <h3 className="mb-3 font-semibold text-slate-900">Agregar aspecto propio</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Nombre del aspecto (ej. Atención al cliente)"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
              />
              <input
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Descripción breve (opcional)"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
              />
            </div>
            <button
              onClick={addCustom}
              disabled={!newLabel.trim()}
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              <Plus size={16} /> Agregar aspecto
            </button>
          </div>

          {/* Guardar */}
          <div className="flex items-center justify-end gap-3">
            {saved && (
              <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-600">
                <CheckCircle2 size={16} /> Configuración guardada
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={saving || enabledCount === 0}
              className="inline-flex items-center gap-2 rounded-lg bg-amber-400 px-6 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-amber-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Save size={16} /> {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
          {enabledCount === 0 && (
            <p className="text-right text-xs text-red-500">Debes mantener al menos un aspecto habilitado.</p>
          )}
        </div>
      )}
    </EmpresaLayout>
  )
}

export default EmpresaAspectos
